import { cn } from "@src/lib/utils/cn";
import { CSSProperties, FC, useEffect, useState } from "react";

type Props = {
  size?: number;
  visibilityDelayMs?: number;
  className?: string;
  style?: CSSProperties;
  centered?: boolean;
  /** Accessible name announced by the status role. */
  label?: string;
};

const Spinner: FC<Props> = ({
  size = 40,
  visibilityDelayMs = 0,
  className,
  style,
  centered = false,
  label = "Loading",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, visibilityDelayMs);
    return () => clearTimeout(timer);
  }, [visibilityDelayMs]);
  if (!isVisible) {
    return null;
  }

  // Brand by default. With no caller colour, a spinner inside a button
  // takes the label colour instead, so it stays visible on the brand-filled
  // primary Button. tailwind-merge drops the default when className sets a
  // text colour, and then the caller's colour wins everywhere.
  const merged = cn("text-action-primary", className);
  const colorClassName = merged.split(/\s+/).includes("text-action-primary")
    ? cn("text-action-primary in-[button]:text-current", className)
    : merged;

  const spinner = (
    <>
      <style>{`
        @keyframes spinner-rotate {
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes spinner-dash {
          0% {
            stroke-dasharray: 1, 150;
            stroke-dashoffset: 0;
          }
          50% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -35;
          }
          100% {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: -124;
          }
        }
      `}</style>
      <div
        role="status"
        aria-label={label}
        style={{ width: `${size}px`, height: `${size}px`, ...style }}
        className={colorClassName}
      >
        <svg
          aria-hidden="true"
          className="z-[2]"
          style={{
            animation: "spinner-rotate 2s linear infinite",
          }}
          viewBox="0 0 50 50"
        >
          <circle
            stroke="currentColor"
            className="z-[2]"
            style={{
              strokeLinecap: "round",
              animation: "spinner-dash 1.5s ease-in-out infinite",
            }}
            cx="25"
            cy="25"
            r="20"
            fill="none"
            strokeWidth="5"
          />
        </svg>
      </div>
    </>
  );
  if (centered) {
    return (
      <div className="flex flex-1 items-center justify-center self-stretch">
        {spinner}
      </div>
    );
  }
  return spinner;
};

export default Spinner;
